/**
 * @file CoreClient - Multi-transport lifecycle entity that talks to a
 * remote server through one or more `CoreTransport` siblings.
 *
 * Rewritten in umbrella #518's phase 1 to replace the previous
 * single-typed-handle shape. The new shape splits the two patterns
 * that used to share the single base — self-managed connections vs.
 * injected transports — into separate base classes: self-managed
 * connections extend `CoreLifecycle` directly (using
 * `runConnectVerifyRollback` inside their `onStart`), while
 * `CoreClient` is exclusively the injected-transport role.
 *
 * `deps.transports` is a record of `CoreTransport` siblings the client
 * uses. Each method body explicitly picks the transport whose protocol
 * suits the call shape — `this.deps.transports.http.request(...)` for
 * a `Promise<T>`-returning HTTP request/response, `this.deps.transports.ws
 * .subscribe(...)` for a `TypedSubscription<C,S>`-returning WebSocket
 * subscription. The dispatch is documented call-site convention rather
 * than proxy / decorator magic (open decision #6 from the umbrella body,
 * settled in the ADR).
 *
 * Each transport is adopted at construction so its logs and metrics bubble
 * up under the client's `fullId`, and `CoreLifecycle`'s child-walk handles
 * configuring and starting them automatically. `onConnect` / `onVerify` /
 * `onDisconnect` hooks are gone — the orchestration that used to live on
 * this base is now `runConnectVerifyRollback`, used inside `CoreLifecycle`
 * subclasses with their own connection state.
 *
 * @example
 * ```typescript
 * class AdminClient extends CoreClient<
 *   { id?: string; baseUrl: string },
 *   { http: HttpClientTransport }
 * > {
 *   async listLearners(): Promise<Learner[]> {
 *     return this.deps.transports.http.request(ListLearnersRoute, {})
 *   }
 * }
 * ```
 *
 * @module
 */

import type { CoreTransport } from './core_transport.ts'
import { CoreLifecycle } from './core_lifecycle.ts'

/**
 * Lifecycle-managed multi-transport client.
 *
 * @typeParam TProps - Constructor props; must include an optional `id`.
 * @typeParam TTransports - Record of `CoreTransport` siblings, keyed by
 *   protocol nickname (`http`, `ws`, future `h2`, `grpc`, …). Used by
 *   subclass method bodies to pick which transport handles each call.
 */
export abstract class CoreClient<
  TProps extends { id?: string } = { id?: string },
  TTransports extends Record<string, CoreTransport> = Record<
    string,
    CoreTransport
  >,
  TEvents extends Record<string, readonly unknown[]> = {},
> extends CoreLifecycle<TProps, TEvents> {
  protected readonly deps: { transports: TTransports }

  /**
   * @param props - Static configuration validated by the subclass schema.
   * @param deps - Runtime dependencies. `transports` is the record of
   *   `CoreTransport` siblings; each is adopted here so the lifecycle
   *   child-walk handles its configure / start / stop.
   */
  constructor(props: TProps, deps: { transports: TTransports }) {
    super(props)
    this.deps = deps
    for (const transport of Object.values(deps.transports)) {
      this.adopt(transport)
    }
  }
}
